import React from 'react';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import { isEmpty } from 'lodash';
import { Meteor } from 'meteor/meteor';

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Tooltip
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LIBRARY_ICON_TYPES } from '../../../api/AnnotationTemplates/AnnotationTemplates';
import { getPublicId } from '../../../api/Maps/map-operations';
import InternationalPhoneNumber from '../InternationalPhoneNumber';

const MapsShareByMobile = ({ MapProps, buttonSettings, annotationVideo }) => {
  const [openModal, setOpenModal] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [changeLocationFieldsState, setChangeLocationFieldsState] = React.useState(false);
  const [changeAnnotationState, setChangeAnnotationState] = React.useState(false);
  const [changeLocationState, setChangeLocationState] = React.useState(false);
  const [completeNumber, setCompleteNumber] = React.useState('');
  const [annotationSelected, setAnnotationSelected] = React.useState('');
  const [sharedAnnotation, setSharedAnnotation] = React.useState(false);

  const renderButtons = () => {
    if (!isEmpty(buttonSettings.shareButtonByMobile.libraryIcon)) {
      if (buttonSettings.shareButtonByMobile.libraryIcon === LIBRARY_ICON_TYPES.FONTAWESOME) {
        return (
          <FontAwesomeIcon
            style={{ color: '2A3F90' }}
            icon={buttonSettings.shareButtonByMobile.iconFontawesome}
            size="lg"
          />
        );
      }
      if (buttonSettings.shareButtonByMobile.libraryIcon === LIBRARY_ICON_TYPES.SVG) {
        return (
          <img
            src={buttonSettings.shareButtonByMobile.iconSvg}
            alt={buttonSettings.shareButtonByMobile.iconFontawesome}
            style={{ color: '2A3F90', height: '20px', marginRight: '5px', width: '20px' }}
          />
        );
      }
    }

    return '';
  };

  const sendMobileToBack = async () => {
    const changes = `${+changeLocationState}${+changeLocationFieldsState}${+changeAnnotationState}`;
    const urlPublicShare = Meteor.absoluteUrl(`shared/maps/${getPublicId(MapProps.map)}`);
    const urlLocation = Meteor.absoluteUrl(
      `sharedLocation/maps/${getPublicId(MapProps.map)}/${changes}/${annotationSelected}`
    );
    const urlToShare =
      sharedAnnotation === true && !isEmpty(annotationSelected) ? urlLocation : urlPublicShare;
    const response = await fetch(
      'https://sd1.ipsgeoprocesing.ipsilum.com/MG/utilities/message_whatsapp',
      {
        method: 'POST',
        body: JSON.stringify({
          message,
          phone: completeNumber,
          url: urlToShare
        }),
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      }
    );
    const respuesta = await response.json();
    if (respuesta && respuesta.status) {
      Bert.alert('Mensaje enviado al móvil', 'success');
    } else {
      Bert.alert(respuesta, 'info');
    }
  };

  const body = (
    <Box
      backgroundColor="white"
      width="60vw"
      height="60vh"
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="center"
      p={3}
      borderRadius="8px"
      boxShadow={3}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="80%"
        p={2}
      >
        <TextField
          id="outlined-required"
          label="Message"
          size="small"
          sx={{ mb: 2 }}
          value={message}
          onChange={event => setMessage(event.target.value)}
          fullWidth
        />
        <InternationalPhoneNumber setCompleteNumber={setCompleteNumber} />
        <Box display="row" justifyContent="space-evenly" fullwidth="true" mb={2}>
          <Checkbox
            checked={sharedAnnotation}
            onChange={() => {
              setSharedAnnotation(!sharedAnnotation);
            }}
            sx={{
              color: '#2A3F90',
              '&.Mui-checked': {
                color: '#2A3F90'
              }
            }}
            value="variable"
          />
          Share the map with an annotation
        </Box>
        {sharedAnnotation === true && (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="demo-select-label">Annotation</InputLabel>
              <Select
                labelId="demo-select-label"
                id="demo-select"
                label="Annotation"
                size="small"
                value={annotationSelected || ''}
                onChange={event => setAnnotationSelected(event.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {annotationVideo.reverse().map(item => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.geoJSON.properties.template.name} - {item._id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Autocomplete
                disablePortal
                options={annotationVideo}
                getOptionLabel={option =>
                  `${option.geoJSON.properties.template.name} - ${option._id}`
                }
                value={annotationVideo.find(item => item._id === annotationSelected) || null}
                onChange={(event, newValue) => setAnnotationSelected(newValue ? newValue._id : '')}
                size="small"
                renderInput={params => <TextField {...params} label="Annotation" />}
              />
            </FormControl>
            <Box display="row" justifyContent="flex-start" fullwidth="true" mb={2}>
              <Checkbox
                checked={changeLocationState}
                onChange={() => setChangeLocationState(!changeLocationState)}
                sx={{
                  color: '#2A3F90',
                  '&.Mui-checked': {
                    color: '#2A3F90'
                  }
                }}
                value="variable"
              />
              {changeLocationState === true
                ? 'Location can be changed'
                : 'Location cannot be changed'}
            </Box>
            <Box display="row" justifyContent="flex-start" fullwidth="true" mb={2}>
              <Checkbox
                checked={changeLocationFieldsState}
                onChange={() => setChangeLocationFieldsState(!changeLocationFieldsState)}
                sx={{
                  color: '#2A3F90',
                  '&.Mui-checked': {
                    color: '#2A3F90'
                  }
                }}
                value="variable"
              />
              {changeLocationFieldsState === true
                ? 'Fields related to the location can be changed.'
                : 'Fields related to the location cannot be changed.'}
            </Box>
            <Box display="row" justifyContent="flex-start" fullwidth="true" mb={2}>
              <Checkbox
                checked={changeAnnotationState}
                onChange={() => setChangeAnnotationState(!changeAnnotationState)}
                sx={{
                  color: '#2A3F90',
                  '&.Mui-checked': {
                    color: '#2A3F90'
                  }
                }}
                value="variable"
              />
              {changeAnnotationState === true
                ? 'Annotation can be updated'
                : 'Annotation cannot be updated'}
            </Box>
          </>
        )}
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" px={2}>
        <Button
          variant="outlined"
          color="success"
          onClick={() => {
            sendMobileToBack();
            setOpenModal(false);
          }}
        >
          Send
        </Button>
        <Button
          variant="outlined"
          sx={{
            color: '#2A3F90',
            backgroundColor: 'white',
            borderColor: '#2A3F90'
          }}
          onClick={() => setOpenModal(false)}
        >
          Close
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {buttonSettings.shareButtonByMobile && !isEmpty(buttonSettings.shareButtonByMobile) && (
        <>
          <Box display="flex" flexDirection="row" gap={2}>
            <Tooltip title={buttonSettings.shareButtonByMobile.name}>
              <Button
                onClick={() => {
                  setOpenModal(true);
                }}
              >
                {buttonSettings.shareButtonByMobile.icon
                  ? buttonSettings.shareButtonByMobile.icon
                  : renderButtons()}
              </Button>
            </Tooltip>
          </Box>

          <Modal
            open={openModal}
            onClose={() => setOpenModal(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {body}
          </Modal>
        </>
      )}
    </>
  );
};

MapsShareByMobile.defaultProps = {
  buttonSettings: {},
  annotationVideo: []
};

MapsShareByMobile.propTypes = {
  buttonSettings: PropTypes.object,
  MapProps: PropTypes.object.isRequired,
  annotationVideo: PropTypes.array
};

export default MapsShareByMobile;
